import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import StudentRequestTable from "../../components/Dashboard/Students/StudentRequestTable";
import Spinner from "../../components/Shared/Spinner";
import { GetRequests } from "../../api/Courses/GetAllRequests";
import Input from "../../components/Shared/Input";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import Button from "../../components/Shared/Button";
import { AcceptAllStudentRequest } from "../../api/Courses/AcceptAllRequest";
import { toast } from "react-toastify";
import { queryClient } from "../../main";

const statusOptions: option[] = [
  { name: "approved" },
  { name: "pending" },
];

const StudentsRequests = () => {
  const [page, setPage] = useState(1);
  const [limit,] = useState(10);
  const [jumpPage, setJumpPage] = useState(1);
  const [status, setStatus] = useState<option | null>(null);
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [courseName, setCourseName] = useState("");

  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName);
  const [debouncedPhone, setDebouncedPhone] = useState(phone);
  const [debouncedCourseName, setDebouncedCourseName] = useState(courseName);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFirstName(firstName);
      setDebouncedPhone(phone);
      setDebouncedCourseName(courseName);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [status, firstName, phone, courseName]);

  const { data: RequestsResponse, isLoading: isStudentsLoading } = useQuery({
    queryFn: () => GetRequests({ page, limit, status: status?.name, firstName: debouncedFirstName, phone: debouncedPhone, courseName: debouncedCourseName }),
    queryKey: ["requests", page, limit, status, debouncedFirstName, debouncedPhone, debouncedCourseName],
    staleTime: 0,
    refetchInterval: 0,
  });

  const requests = RequestsResponse?.data?.requests || [];
  const metaData = RequestsResponse?.data?.metaData;

  const { mutate, isPending } = useMutation({
    mutationFn: AcceptAllStudentRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success('All requests accepted successfully');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error accepting requests';
      toast.error(errorMessage);
    },
  });

  const handleNextPage = () => setPage((prev) => prev + 1);
  const handlePreviousPage = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleJumpPage = () => setPage(jumpPage);
  const handleClearStatus = () => setStatus(null);

  const handleAcceptAllRequests = () => {
    const requestIds = requests.map(request => request.id);
    mutate({ requestIds });
  };

  if (isStudentsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">Course Requests</h1>
      <div className='p-8 rounded-xl bg-white border-black/15 shadow'>
        <h1 className='font-bold text-2xl mb-8'>Requests Details</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="relative">
            <Selectbox
              selectOptions={statusOptions}
              selected={status}
              setSelected={setStatus}
              placeholder="Select Status"
              className="!rounded"
              titleStyles="!mb-0"
            />
            {status && (
              <button
                onClick={handleClearStatus}
                className="absolute top-1/2 right-4 font-bold transform -translate-y-1/2 text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <Input
            name="FirstName"
            type="text"
            className="md:!space-y-0"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First Name"
            inputStyles="!rounded !h-[50px]"
          />
          <Input
            name="Phone"
            type="text"
            className="md:!space-y-0"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone"
            inputStyles="!rounded !h-[50px]"
          />
          <Input
            name="CourseName"
            type="text"
            className="md:!space-y-0"
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            placeholder="Course Name"
            inputStyles="!rounded !h-[50px]"
          />
        </div>
        <Button 
          variant="primary" 
          className="!h-[50px] !rounded-md w-72 !text-lg mb-5 ml-auto" 
          onClick={handleAcceptAllRequests}
        >
          Accept All Requests {isPending && <Spinner/>}
        </Button>
        <StudentRequestTable data={requests} />

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handlePreviousPage}
            disabled={page === 1}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Previous
          </button>
          <span>
            Page {page} of {metaData?.totalPages || 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!metaData || page >= metaData.totalPages}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Next
          </button>
        </div>

        <div className="flex justify-center items-center mt-4">
          <Input
            name="Page"
            type="number"
            value={jumpPage.toString()}
            className="md:!space-y-0"
            inputStyles="!h-10 !rounded"
            onChange={(e) => setJumpPage(Number(e.target.value))}
          />
          <button
            onClick={handleJumpPage}
            className="bg-blue-500 text-white px-4 h-10 rounded disabled:bg-gray-400"
          >
            Jump to Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentsRequests;
